import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import {
  resolveStoreBillingDeleteWarning,
  type StoreBillingDeleteWarningSource,
} from "@pulse/shared";
import { cn } from "@/lib/utils";
import { fetchConsumerEntitlementsRpc } from "@/lib/consumerEntitlements";

type DeleteAccountModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userEmail: string;
  onConfirmDelete: () => Promise<void>;
  /** Test / story override — skips entitlements fetch when set (including null). */
  storeBillingWarning?: StoreBillingDeleteWarningSource | null;
};

export function DeleteAccountModal({
  open,
  onOpenChange,
  userEmail,
  onConfirmDelete,
  storeBillingWarning: storeBillingWarningProp,
}: DeleteAccountModalProps) {
  const { t } = useTranslation("consumer");
  const ns = "settings.deleteAccountModal" as const;
  const [step, setStep] = useState<1 | 2>(1);
  const [emailInput, setEmailInput] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [storeBillingWarning, setStoreBillingWarning] = useState<
    StoreBillingDeleteWarningSource | null
  >(null);

  const emailMatches = emailInput.trim().toLowerCase() === userEmail.trim().toLowerCase();

  const bullets = [
    t(`${ns}.bulletProfile`),
    t(`${ns}.bulletSquads`),
    t(`${ns}.bulletInterests`),
    t(`${ns}.bulletRatings`),
    t(`${ns}.bulletTickets`),
  ];

  useEffect(() => {
    if (!open) {
      setStep(1);
      setEmailInput("");
      setDeleting(false);
      setError(null);
      setStoreBillingWarning(null);
      return;
    }

    if (storeBillingWarningProp !== undefined) {
      setStoreBillingWarning(storeBillingWarningProp);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const resolved = await fetchConsumerEntitlementsRpc();
        if (cancelled) return;
        setStoreBillingWarning(
          resolveStoreBillingDeleteWarning(resolved.subscriptionSource, resolved.accessKind),
        );
      } catch {
        if (!cancelled) setStoreBillingWarning(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, storeBillingWarningProp]);

  function handleClose() {
    if (deleting) return;
    onOpenChange(false);
  }

  async function handleFinalDelete() {
    if (!emailMatches || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await onConfirmDelete();
    } catch (err) {
      setError(err instanceof Error ? err.message : t(`${ns}.deleteFailed`));
      setDeleting(false);
    }
  }

  const iapWarning =
    storeBillingWarning === "apple"
      ? t(`${ns}.iapBillingWarningApple`)
      : storeBillingWarning === "google"
        ? t(`${ns}.iapBillingWarningGoogle`)
        : null;

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden
      />
      <div className="fixed inset-0 z-[90] flex items-center justify-center px-6">
        <div
          className="w-full max-w-md rounded-3xl border border-border bg-background p-6 shadow-2xl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-account-title"
        >
          {step === 1 ? (
            <>
              <h2 id="delete-account-title" className="text-base font-bold text-foreground">
                {t(`${ns}.title`)}
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                {t(`${ns}.intro`)}
              </p>
              <ul className="mt-3 space-y-1.5 text-sm text-foreground">
                {bullets.map((line) => (
                  <li key={line} className="flex items-start gap-2">
                    <span className="mt-0.5 text-muted-foreground">•</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              {iapWarning ? (
                <div
                  role="alert"
                  className="mt-4 rounded-2xl border border-amber-500/50 bg-amber-500/15 px-4 py-3 text-sm font-semibold leading-relaxed text-amber-100"
                >
                  {iapWarning}
                </div>
              ) : null}
              <p className="mt-4 text-xs font-medium text-red-400/80">
                {t(`${ns}.irreversible`)}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="rounded-xl bg-red-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-600"
                >
                  {t(`${ns}.deleteFinal`)}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-xl border border-border px-4 py-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:bg-bg-surface"
                >
                  {t(`${ns}.cancel`)}
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 id="delete-account-title" className="text-base font-bold text-foreground">
                {t(`${ns}.confirmationTitle`)}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground">
                {t(`${ns}.confirmationBody`)}
              </p>
              {iapWarning ? (
                <div
                  role="alert"
                  className="mt-4 rounded-2xl border border-amber-500/50 bg-amber-500/15 px-4 py-3 text-sm font-semibold leading-relaxed text-amber-100"
                >
                  {iapWarning}
                </div>
              ) : null}
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder={userEmail}
                autoComplete="email"
                disabled={deleting}
                className={cn(
                  "mt-4 w-full rounded-2xl border border-border bg-bg-surface px-4 py-3 text-sm text-foreground outline-none",
                  "focus:border-red-500/40 focus:ring-1 focus:ring-red-500/20",
                  deleting && "opacity-60",
                )}
              />
              {error && <p className="mt-3 text-sm text-red-400">{error}</p>}
              <div className="mt-6 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => void handleFinalDelete()}
                  disabled={!emailMatches || deleting}
                  className="inline-flex items-center gap-2 rounded-xl bg-red-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t(`${ns}.confirmDelete`)}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={deleting}
                  className="rounded-xl border border-border px-4 py-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:bg-bg-surface disabled:opacity-40"
                >
                  {t(`${ns}.cancel`)}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
